import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { machineLabel } from '@/lib/present';
import { AlertTriangle, CheckCircle2, Clock, Database, XCircle, Info, HardDrive } from 'lucide-react';
import { COVERAGE_LABELS, DEVIATION_STATUS_LABELS, SEVERITY_LABELS, SOURCE_STATUS_LABELS } from '@/lib/labels';

const BASE = 'inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[13px] font-medium leading-5';

const SOURCE_STYLES = {
  demo: 'bg-[var(--hr-inset)] text-[var(--hr-text)] border-[var(--hr-hairline)]',
  updated: 'bg-[var(--hr-green-bg)] text-[var(--hr-green)] border-[rgba(6,118,71,0.25)]',
  delayed: 'bg-[var(--hr-amber-bg)] text-[var(--hr-amber)] border-[rgba(154,91,0,0.35)] border-dashed',
  partial: 'bg-[var(--hr-amber-bg)] text-[var(--hr-amber)] border-[rgba(154,91,0,0.35)]',
  error: 'bg-[var(--hr-red-bg)] text-[var(--hr-red)] border-[rgba(180,35,24,0.25)]',
};
const SOURCE_ICONS = { demo: HardDrive, updated: CheckCircle2, delayed: Clock, partial: AlertTriangle, error: XCircle };

export function SourceStatusBadge({ status, className }) {
  const Icon = SOURCE_ICONS[status] ?? Info;
  return (
    <span data-testid={`source-status-badge-${status}`} className={cn(BASE, SOURCE_STYLES[status] ?? SOURCE_STYLES.demo, className)}>
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {SOURCE_STATUS_LABELS[status] ?? status}
    </span>
  );
}

const SEVERITY_STYLES = {
  high: 'bg-[var(--hr-red-bg)] text-[var(--hr-red)] border-[rgba(180,35,24,0.25)]',
  medium: 'bg-[var(--hr-amber-bg)] text-[var(--hr-amber)] border-[rgba(154,91,0,0.35)]',
  info: 'bg-[var(--hr-blue-bg)] text-[var(--hr-blue)] border-[rgba(35,64,144,0.2)]',
};
const SEVERITY_ICONS = { high: XCircle, medium: AlertTriangle, info: Info };

export function SeverityBadge({ severity, className }) {
  const Icon = SEVERITY_ICONS[severity] ?? Info;
  return (
    <span data-testid={`severity-badge-${severity}`} className={cn(BASE, SEVERITY_STYLES[severity] ?? SEVERITY_STYLES.info, className)}>
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {SEVERITY_LABELS[severity] ?? severity}
    </span>
  );
}

const DEV_STATUS_STYLES = {
  open: 'bg-[var(--hr-inset)] text-[var(--hr-text)] border-[var(--hr-hairline)]',
  reviewing: 'bg-[var(--hr-blue-bg)] text-[var(--hr-blue)] border-[rgba(35,64,144,0.2)]',
  resolved: 'bg-[var(--hr-green-bg)] text-[var(--hr-green)] border-[rgba(6,118,71,0.25)]',
};

export function DeviationStatusBadge({ status, className }) {
  return (
    <span data-testid={`deviation-status-badge-${status}`} className={cn(BASE, DEV_STATUS_STYLES[status] ?? DEV_STATUS_STYLES.open, className)}>
      <span className={cn('h-1.5 w-1.5 rounded-full', status === 'resolved' ? 'bg-[var(--hr-green)]' : status === 'reviewing' ? 'bg-[var(--hr-blue)]' : 'bg-[var(--hr-muted-2)]')} aria-hidden="true" />
      {DEVIATION_STATUS_LABELS[status] ?? status}
    </span>
  );
}

const COVERAGE_STYLES = {
  complete: SOURCE_STYLES.updated,
  partial: SOURCE_STYLES.partial,
  missing: SOURCE_STYLES.error,
  not_applicable: SOURCE_STYLES.demo,
  planned_off: SOURCE_STYLES.demo,
  no_data: SOURCE_STYLES.demo,
};

export function CoverageBadge({ status, label, className }) {
  return (
    <span data-testid={`coverage-badge-${status}`} className={cn(BASE, 'hr-tabular', COVERAGE_STYLES[status] ?? SOURCE_STYLES.demo, className)}>
      {label ?? COVERAGE_LABELS[status] ?? status}
    </span>
  );
}

/**
 * Maskinreferanse: nøytral kort-ID (presentasjonslag) + valgfritt navn. Lenker når `to` er satt.
 * Den fulle, underliggende ID-en beholdes i ruter og data-testid for sporbarhet.
 */
export function MachineRef({ machineId, name, to, className, testId, size = 'sm' }) {
  const label = machineLabel(machineId);
  const inner = (
    <>
      <span className={cn('hr-tabular inline-flex items-center rounded-md border border-[var(--hr-hairline)] bg-[var(--hr-inset)] px-1.5 font-semibold leading-5 text-[var(--hr-heading)]', size === 'sm' ? 'text-[13px]' : 'text-sm')}>{label}</span>
      {name && <span className={cn('truncate', size === 'sm' ? 'text-sm' : 'text-base', to ? 'text-[var(--hr-heading)]' : 'text-[var(--hr-text)]')}>{name}</span>}
    </>
  );
  if (to) {
    return (
      <Link to={to} data-testid={testId} aria-label={`Åpne ${label}${name ? ` ${name}` : ''}`} className={cn('group/ref inline-flex max-w-full items-center gap-2 rounded-md underline-offset-[3px] transition-colors duration-150 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]', className)}>
        {inner}
      </Link>
    );
  }
  return (
    <span data-testid={testId} className={cn('inline-flex max-w-full items-center gap-2', className)}>
      {inner}
    </span>
  );
}

export function Chip({ children, className, ...props }) {
  return (
    <span className={cn(BASE, 'border-[var(--hr-hairline)] bg-[var(--hr-surface-2)] text-[var(--hr-text)]', className)} {...props}>
      {children}
    </span>
  );
}

/** Diskret, rolig merknad. Bruk amber for usikkerhet/delvis, rød kun for alvorlig. */
export function Note({ children, tone = 'neutral', className, icon = true, ...props }) {
  const styles = {
    neutral: 'border-[var(--hr-hairline)] bg-[var(--hr-surface-2)] text-[var(--hr-text)]',
    amber: 'border-[rgba(154,91,0,0.25)] bg-[var(--hr-amber-bg)] text-[#5C3700]',
    red: 'border-[rgba(180,35,24,0.25)] bg-[var(--hr-red-bg)] text-[#7A1A12]',
    green: 'border-[rgba(6,118,71,0.25)] bg-[var(--hr-green-bg)] text-[#05512F]',
    info: 'border-[rgba(35,64,144,0.18)] bg-[var(--hr-blue-bg)] text-[#172B5C]',
  };
  const Icon = tone === 'amber' ? AlertTriangle : tone === 'red' ? XCircle : tone === 'green' ? CheckCircle2 : tone === 'info' ? Database : Info;
  return (
    <div className={cn('flex items-start gap-2.5 rounded-[var(--hr-radius-sm)] border px-4 py-3 text-[15px] leading-6', styles[tone], className)} {...props}>
      {icon && <Icon className="mt-1 h-4 w-4 shrink-0" aria-hidden="true" />}
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

/** Nøkkelverdi for datadekning: meter + tekst (høy/middels/lav). Farge er aldri eneste bærer. */
export function CoverageMeter({ received, expected, label, className, testId }) {
  const pct = expected > 0 ? Math.round((received / expected) * 100) : 0;
  const level = pct >= 95 ? 'Høy' : pct >= 80 ? 'Middels' : 'Lav';
  const color = pct >= 95 ? 'bg-[var(--hr-green)]' : pct >= 80 ? 'bg-[var(--hr-amber)]' : 'bg-[var(--hr-red)]';
  return (
    <div className={cn('min-w-0', className)} data-testid={testId}>
      <div className="flex items-baseline justify-between gap-2 text-[13px]">
        <span className="truncate text-[var(--hr-muted)]">{label}</span>
        <span className="hr-tabular shrink-0 font-medium text-[var(--hr-text)]">
          {received}/{expected} · {level}
        </span>
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-[var(--hr-inset)]" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={`${label}: ${pct} % dekning`}>
        <div className={cn('h-full rounded-full', color)} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
